import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { navigate } from 'gatsby-link';
import {
  OrderDetailsService, CommerceService, PageStateContext, OrderDetails,
} from '../../../services';
import {
  billingAddressFieldName,
  defaultErrorMessage,
  defaultOrderDetailsError,
  formSubmitEvent,
  getCheckoutSteps,
  isRequiredMessage,
  mustBeValidMessage,
  selectChangeEventName,
  shippingAddressFieldName,
  successMessage,
} from './constants';
import { Address } from './address';
import { useCart } from '../cart/hooks/use-cart';
import { useOrderDetails } from './hooks/use-order-details';
import { AdobeTracking } from '../../../tracking';

const handleFieldPrefill = {
  /* eslint-disable @typescript-eslint/naming-convention */
  'roche-input': (field: HTMLRocheInputElement, orderDetails: OrderDetails) :void => {
    const fieldData = orderDetails?.data?.[field.name];
    if (!fieldData) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setTimeout(() => (field as any).setValue?.(orderDetails.data[field.name]), 200);
  },
  'roche-select': (field: HTMLRocheSelectElement, orderDetails: OrderDetails) :void => {
    const data = orderDetails?.data?.[field.name];
    const options: Record<'value', string>[] = JSON.parse(field.options || []);
    const selectedOption = options.findIndex((option) => option.value === data);

    if (selectedOption < 0) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (field as any).shadowRoot
      ?.querySelectorAll('.item')
      .forEach(((option: HTMLElement) => setTimeout(() => option.click(), 200)));
  },
  'roche-dropdown': (field: HTMLRocheDropdownElement, orderDetails: OrderDetails) :void => {
    const data = orderDetails?.data?.[field.name];
    const options: Record<'value', string>[] = JSON.parse(field.options || []);
    const selectedOption = options.findIndex((option) => option.value === data);

    // eslint-disable-next-line no-param-reassign
    field.selected = selectedOption;
  },
  /* eslint-enable @typescript-eslint/naming-convention */
};

export const OrderDetailsForm = (): JSX.Element => {
  const { cartLink } = useContext(PageStateContext);
  const [error, setError] = useState(defaultErrorMessage);
  const [shouldMatchAddresses, setShouldMatchAddresses] = useState(false);
  const formElement = useRef(null);
  const addressMatchControlElement = useRef(null);
  const { cart, cartError, isFetchingCart } = useCart();
  const { orderDetails, orderDetailsError, isFetchingOrderDetails } = useOrderDetails();
  const [hasOrderDetails, setHasOrderDetails] = useState(false);
  const updatedData = {
    currency: 'EUR',
    items: cart.map((product) => ({
      item_id: product.productMetadataPageUniqueId,
      item_name: product?.name,
      currency: 'EUR',
      discount: '',
      item_brand: 'Roche',
      item_category: 'Product',
      item_variant: '',
      price: product?.price,
      quantity: product.quantity,
    })),
  };

  useEffect(() => {
    if (orderDetailsError) {
      return;
    }
    formElement.current?.querySelectorAll('roche-input, roche-dropdown, roche-select')
      .forEach((field: HTMLInputElement) => {
        handleFieldPrefill[field.tagName.toLocaleLowerCase()]?.(field, orderDetails);
      });
  }, [formElement.current]);

  useEffect(() => {
    const handleMatchingAddressChanges = (event: CustomEvent) => {
      setShouldMatchAddresses((event.target as HTMLElement)?.shadowRoot?.querySelector('.input.input--checked') === null);
      /*
       * Given the disconnect between Stencil and React,
       * we must push any DOM operations to end of the call stack
       */
      setTimeout(() => formElement.current?.updateFormFields?.(), 200);
    };
    addressMatchControlElement
      .current?.addEventListener(selectChangeEventName, handleMatchingAddressChanges);

    return () => {
      addressMatchControlElement
        .current?.removeEventListener(selectChangeEventName, handleMatchingAddressChanges);
    };
  }, [addressMatchControlElement.current]);

  useEffect(() => {
    const handleFormSubmit = async () => {
      try {
        const details = await formElement.current.getFormData() as string;
        await OrderDetailsService.saveDetails(JSON.parse(details));
        setHasOrderDetails(true);
        navigate('/order-confirmation');
      } catch {
        setError(defaultOrderDetailsError);
      }
    };
    window.addEventListener(formSubmitEvent, handleFormSubmit);

    return () => {
      window.removeEventListener(formSubmitEvent, handleFormSubmit);
    };
  }, []);

  useEffect(() => {
    if (cart.length > 0) {
      AdobeTracking(updatedData, 'scCheckout');
    }
  }, [cart]);

  useEffect(() => {
    const isInvalidCheckoutAttempt = (cart.length <= 0 && !isFetchingCart) && !hasOrderDetails;
    if (cartError || isInvalidCheckoutAttempt) {
      navigate(cartLink as string);
    }
  }, [isFetchingCart, isFetchingOrderDetails]);

  if ((isFetchingCart || isFetchingOrderDetails) && !hasOrderDetails) {
    return <roche-loading-spinner />;
  }

  return (
    <roche-grid layout="8-4">
      <roche-journey
        slot="left"
        current-step={1}
        steps={JSON.stringify(getCheckoutSteps(cartLink as string))}
      />
      <roche-headline
        slot="left"
        level={2}
        headline="Persönliche Daten"
      />
      <roche-form
        ref={formElement}
        slot="left"
        action={CommerceService.endpoints.placeOrder}
        submit="Bestellung prüfen"
        success={successMessage}
        error={error}
        disabled={hasOrderDetails}
        disable-recaptcha="true"
        disable-submit="true"
      >

        <roche-input
          label="Nachname"
          placeholder="Nachname"
          type="text"
          name="last-name"
          required
          error={isRequiredMessage}
        />
        <roche-input
          label="Vorname"
          placeholder="Vorname"
          type="text"
          name="first-name"
          required
          error={isRequiredMessage}
        />
        <roche-input
          label="Telefonnummer"
          placeholder="Telefonnummer"
          name="telephone"
          type="tel"
          required
          error={isRequiredMessage}
        />

        <roche-input
          label="E-Mail Adresse"
          placeholder="E-Mail Adresse"
          name="email"
          type="email"
          required
          error={mustBeValidMessage}
        />

        <roche-grid layout="6-6">
          <roche-input
            slot="left"
            label="Firma"
            placeholder="Firma"
            type="text"
            name="company"
            required
            error={isRequiredMessage}
          />

          <roche-input
            slot="right"
            label="Abteilung"
            placeholder="Abteilung"
            type="text"
            name="department"
            required
            error={isRequiredMessage}
          />

          <roche-input
            slot="left"
            label="Kundennummer/EAN"
            placeholder="Kundennummer/EAN"
            type="text"
            name="account-ean"
          />

          <roche-input
            slot="right"
            label="PO-Nummer"
            placeholder="PO-Nummer"
            type="text"
            name="customer-po"
          />
        </roche-grid>

        <roche-input
          label="Anmerkungen"
          placeholder="Anmerkungen"
          type="textarea"
          name="notes"
          max-length="600"
        />

        {[shippingAddressFieldName, billingAddressFieldName]
          .map((prefix, index) => (
            <Address
              key={index}
              prefix={prefix}
              addressMatchControlElement={addressMatchControlElement}
              shouldMatchAddresses={shouldMatchAddresses}
              billingAddressFieldName={billingAddressFieldName}
              shippingAddressFieldName={shippingAddressFieldName}
            />
          ))}
      </roche-form>
    </roche-grid>
  );
};
